import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { createExperimentMutation } from 'api/mutations/experiments';
import { addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { ExperimentType } from 'modules/graphql';

type ExperimentCreation = {
  campaignId: number;
  onSuccess?: (experimentId: number) => void;
  onError?: () => void;
};

const source = 'useExperimentCreation';

export const useExperimentCreation = ({ campaignId, onSuccess, onError }: ExperimentCreation) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to create experiment for campaign ${campaignId}`;
  const errorToasterMessage = t('errorMessage.experimentCreation');

  const errorHandler = (error: unknown) => {
    if (onError) onError();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [createExperiment] = useMutation(createExperimentMutation, {
    onError: errorHandler,
  });

  return async (experimentName: string, experimentType: ExperimentType) => {
    const { data } = await createExperiment({
      variables: { campaignId, name: experimentName, experimentType },
    });

    if (data?.createExperiment?.id) {
      if (onSuccess) onSuccess(data.createExperiment.id);
    }

    if (data?.createExperiment?.message) {
      errorHandler(data.createExperiment.message);
    }
  };
};
